import { useTranslation } from 'react-i18next';
import type { ProjectCustomDomainInfo } from '@claude-code-api/shared';
import { CircleHelp, Copy, Info, ListChecks } from 'lucide-react';
import { toast } from 'sonner';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui';

const STEP_KEYS = ['choose', 'configure', 'verify', 'live'] as const;

interface ProjectCustomDomainHelpDialogProps {
  domain: ProjectCustomDomainInfo | null;
  currentUrl: string | null;
}

export function ProjectCustomDomainHelpDialog({
  domain,
  currentUrl,
}: ProjectCustomDomainHelpDialogProps) {
  const { t } = useTranslation('projects');
  const currentRecords = domain
    ? [
        {
          label: t('deployments.customDomain.help.currentHostname'),
          value: domain.hostname,
        },
        {
          label: t('deployments.customDomain.recordName'),
          value: domain.setupRecord.name,
        },
        {
          label: t('deployments.customDomain.recordValue'),
          value: domain.setupRecord.value,
        },
      ]
    : [];

  const copy = async (label: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(t('deployments.customDomain.help.copied', { label }));
    } catch {
      toast.error(t('deployments.customDomain.errors.copy'));
    }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 rounded-full border border-border/70 bg-background/70 text-muted-foreground"
          aria-label={t('deployments.customDomain.help.open')}
        >
          <CircleHelp className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] w-[calc(100vw-2rem)] max-w-2xl overflow-y-auto p-0">
        <div className="border-b bg-gradient-to-br from-primary/10 via-background to-background px-5 py-6 sm:px-7">
          <DialogHeader className="pr-8 text-left">
            <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-xl border border-primary/20 bg-primary/10 text-primary">
              <ListChecks className="h-5 w-5" />
            </div>
            <DialogTitle>{t('deployments.customDomain.help.title')}</DialogTitle>
            <DialogDescription className="max-w-xl">
              {t('deployments.customDomain.help.description')}
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="space-y-6 px-5 pb-6 sm:px-7">
          {currentUrl && (
            <div className="rounded-xl border bg-muted/35 p-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {t('deployments.customDomain.currentSessionUrl')}
              </p>
              <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
                <code className="min-w-0 flex-1 break-all text-sm">{currentUrl}</code>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-fit"
                  aria-label={t('deployments.customDomain.help.copyField', {
                    label: t('deployments.customDomain.currentSessionUrl'),
                  })}
                  onClick={() => void copy(t('deployments.customDomain.currentSessionUrl'), currentUrl)}
                >
                  <Copy className="h-3.5 w-3.5" />
                  {t('deployments.customDomain.copy')}
                </Button>
              </div>
            </div>
          )}

          <ol className="space-y-4">
            {STEP_KEYS.map((step, index) => (
              <li key={step} className="grid grid-cols-[2rem_minmax(0,1fr)] gap-3">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground">
                  {index + 1}
                </span>
                <div className="pt-0.5">
                  <p className="font-medium">{t(`deployments.customDomain.help.steps.${step}.title`)}</p>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                    {t(`deployments.customDomain.help.steps.${step}.description`)}
                  </p>
                  {step === 'configure' && (
                    domain ? (
                      <div className="mt-3 overflow-hidden rounded-xl border border-primary/20 bg-primary/5">
                        <div className="flex items-center justify-between border-b border-primary/15 px-3 py-2">
                          <span className="text-xs font-semibold uppercase tracking-wider text-primary">
                            {t('deployments.customDomain.help.currentTitle')}
                          </span>
                        </div>
                        <div className="divide-y divide-border/70">
                          {currentRecords.map((record) => (
                            <div key={record.label} className="grid gap-1 px-3 py-2.5 sm:grid-cols-[8rem_minmax(0,1fr)_auto] sm:items-center sm:gap-3">
                              <span className="text-xs font-medium text-muted-foreground">
                                {record.label}
                              </span>
                              <code className="break-all text-xs sm:text-sm">{record.value}</code>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-7 w-fit px-2 text-xs"
                                aria-label={t('deployments.customDomain.help.copyField', { label: record.label })}
                                onClick={() => void copy(record.label, record.value)}
                              >
                                <Copy className="h-3.5 w-3.5" />
                                {t('deployments.customDomain.copy')}
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="mt-3 grid gap-2 rounded-lg border border-dashed border-primary/25 bg-primary/5 p-3 font-mono text-xs sm:grid-cols-[auto_minmax(0,1fr)] sm:text-sm">
                        <span className="font-sans font-medium text-muted-foreground">
                          {t('deployments.customDomain.help.cnameType')}
                        </span>
                        <span>CNAME</span>
                        <span className="font-sans font-medium text-muted-foreground">
                          {t('deployments.customDomain.help.cnameName')}
                        </span>
                        <span>app</span>
                        <span className="font-sans font-medium text-muted-foreground">
                          {t('deployments.customDomain.help.cnameValue')}
                        </span>
                        <span className="break-all">cd-&lt;token&gt;.domains.your-platform.com</span>
                      </div>
                    )
                  )}
                </div>
              </li>
            ))}
          </ol>

          <div className="flex items-start gap-3 rounded-xl border border-warning/25 bg-warning/10 p-4">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
            <div>
              <p className="text-sm font-medium">{t('deployments.customDomain.help.sessionTitle')}</p>
              <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                {t('deployments.customDomain.help.sessionDescription')}
              </p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
